/**
 * Custom chart for case studies — SVG draws the shapes (path, area, bars),
 * HTML positioned absolutely over the SVG renders the axis labels. This
 * keeps the chart visually responsive *and* keeps the text legible at any
 * viewport, since HTML text doesn't shrink with the SVG viewBox.
 */
export default function CaseStudyChart({ data }) {
  if (!data) return null;
  if (data.type === "line") return <LineChart data={data} />;
  if (data.type === "bar") return <BarChart data={data} />;
  return null;
}

// SVG viewBox coordinates (used only by the SVG shapes)
const VB_W = 600;
const VB_H = 320;
const PAD_L = 60;
const PAD_R = 24;
const PAD_T = 30;
const PAD_B = 44;
const INNER_W = VB_W - PAD_L - PAD_R; // 516
const INNER_H = VB_H - PAD_T - PAD_B; // 246

// Convert viewBox coords to CSS percentage of the chart wrapper
const xPct = (vbX) => `${(vbX / VB_W) * 100}%`;
const yPct = (vbY) => `${(vbY / VB_H) * 100}%`;

function LineChart({ data }) {
  const { points, yLabel, xLabel, peakLabel } = data;
  const maxY = Math.max(...points.map((p) => p.y));
  const stepX = INNER_W / (points.length - 1);

  // Path string in viewBox coords
  const path = points
    .map((p, i) => {
      const x = PAD_L + i * stepX;
      const y = PAD_T + INNER_H - (p.y / maxY) * INNER_H;
      return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");
  const areaPath = `${path} L ${PAD_L + INNER_W} ${PAD_T + INNER_H} L ${PAD_L} ${PAD_T + INNER_H} Z`;

  // Y-axis ticks (5 lines)
  const yTicks = Array.from({ length: 5 }, (_, i) => {
    const value = (maxY / 4) * (4 - i);
    const vbY = PAD_T + (INNER_H / 4) * i;
    return { value, vbY };
  });

  // X-axis labels — show ~6 of them, always include first + last
  const xLabelEvery = Math.max(1, Math.ceil(points.length / 6));
  const xLabels = points.map((p, i) => {
    const show = i === 0 || i === points.length - 1 || i % xLabelEvery === 0;
    return { ...p, show, vbX: PAD_L + i * stepX };
  });

  return (
    <ChartFrame yLabel={yLabel} xLabel={xLabel} yTicks={yTicks} xLabels={xLabels}>
      <svg viewBox={`0 0 ${VB_W} ${VB_H}`} preserveAspectRatio="none" className="absolute inset-0 w-full h-full" aria-hidden="true">
        <defs>
          <linearGradient id="lcGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#22D3EE" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#22D3EE" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="lcLine" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#22D3EE" />
            <stop offset="100%" stopColor="#A855F7" />
          </linearGradient>
        </defs>

        {/* Horizontal grid lines */}
        {yTicks.map((t, i) => (
          <line key={i} x1={PAD_L} x2={PAD_L + INNER_W} y1={t.vbY} y2={t.vbY} stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
        ))}

        <path d={areaPath} fill="url(#lcGrad)" />
        <path d={path} fill="none" stroke="url(#lcLine)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />

        {/* End-point marker pulse */}
        {(() => {
          const last = points.length - 1;
          const x = PAD_L + last * stepX;
          const y = PAD_T + INNER_H - (points[last].y / maxY) * INNER_H;
          return (
            <g>
              <circle cx={x} cy={y} r="10" fill="#A855F7" opacity="0.2" className="animate-pulse" />
              <circle cx={x} cy={y} r="5" fill="#A855F7" stroke="#0B0B0E" strokeWidth="2" vectorEffect="non-scaling-stroke" />
              <line x1={x} x2={x} y1={PAD_T} y2={PAD_T + INNER_H} stroke="rgba(168, 85, 247, 0.25)" strokeDasharray="3 3" strokeWidth="1" vectorEffect="non-scaling-stroke" />
            </g>
          );
        })()}
      </svg>

      {/* Peak annotation (HTML, top-right of plot) */}
      {peakLabel && (
        <div
          className="absolute z-[1] text-[10px] sm:text-[11px] font-mono-acc text-purple-300 px-1.5 py-0.5 rounded bg-[#0B0B0E]/70 backdrop-blur-sm border border-purple-400/20"
          style={{
            left: `calc(${xPct(PAD_L + (points.length - 1) * stepX)} - 6px)`,
            top: `calc(${yPct(PAD_T)} - 2px)`,
            transform: "translate(-100%, 0)",
          }}
        >
          {peakLabel}
        </div>
      )}
    </ChartFrame>
  );
}

function BarChart({ data }) {
  const { bars, yLabel, xLabel } = data;
  const maxY = Math.max(...bars.map((b) => b.y));
  const barW = (INNER_W / bars.length) * 0.62;
  const gap = (INNER_W / bars.length) * 0.38;

  // Y-axis ticks
  const yTicks = Array.from({ length: 5 }, (_, i) => {
    const value = (maxY / 4) * (4 - i);
    const vbY = PAD_T + (INNER_H / 4) * i;
    return { value, vbY };
  });

  // X labels (one per bar)
  const xLabels = bars.map((b, i) => {
    const vbX = PAD_L + i * (barW + gap) + gap / 2 + barW / 2;
    return { ...b, show: true, vbX };
  });

  // Bar value labels (HTML chips above each bar)
  const valueLabels = bars.map((b, i) => {
    const h = (b.y / maxY) * INNER_H;
    const vbY = PAD_T + INNER_H - h;
    const vbX = PAD_L + i * (barW + gap) + gap / 2 + barW / 2;
    return { ...b, vbX, vbY };
  });

  return (
    <ChartFrame yLabel={yLabel} xLabel={xLabel} yTicks={yTicks} xLabels={xLabels}>
      <svg viewBox={`0 0 ${VB_W} ${VB_H}`} preserveAspectRatio="none" className="absolute inset-0 w-full h-full" aria-hidden="true">
        <defs>
          <linearGradient id="bcBar" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#A855F7" />
            <stop offset="100%" stopColor="#22D3EE" />
          </linearGradient>
        </defs>

        {/* Horizontal grid lines */}
        {yTicks.map((t, i) => (
          <line key={i} x1={PAD_L} x2={PAD_L + INNER_W} y1={t.vbY} y2={t.vbY} stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
        ))}

        {bars.map((b, i) => {
          const x = PAD_L + i * (barW + gap) + gap / 2;
          const h = (b.y / maxY) * INNER_H;
          const y = PAD_T + INNER_H - h;
          const isLast = i === bars.length - 1;
          const opacity = isLast ? 1 : 0.55 + (i / bars.length) * 0.45;
          return <rect key={i} x={x} y={y} width={barW} height={h} fill="url(#bcBar)" rx="4" opacity={opacity} />;
        })}
      </svg>

      {/* HTML value labels above each bar */}
      {valueLabels.map((v, i) => (
        <div
          key={i}
          className="absolute z-[1] text-[10px] sm:text-[11px] font-mono-acc text-white whitespace-nowrap"
          style={{
            left: xPct(v.vbX),
            top: `calc(${yPct(v.vbY)} - 18px)`,
            transform: "translate(-50%, 0)",
          }}
        >
          {formatK(v.y)}
        </div>
      ))}
    </ChartFrame>
  );
}

// Shared visual frame: aspect-ratio container + HTML-positioned axis labels.
function ChartFrame({ yLabel, xLabel, yTicks, xLabels, children }) {
  return (
    <div className="relative w-full" style={{ aspectRatio: `${VB_W} / ${VB_H}` }}>
      {/* SVG shapes layer */}
      {children}

      {/* Y-axis tick labels (right-aligned to the left padding) */}
      {yTicks.map((t, i) => (
        <div
          key={i}
          className="absolute z-[1] text-right text-[10px] sm:text-[11px] font-mono-acc text-zinc-500 whitespace-nowrap"
          style={{
            right: `calc(100% - ${xPct(PAD_L - 6)})`,
            top: `calc(${yPct(t.vbY)} - 7px)`,
          }}
        >
          {formatK(t.value)}
        </div>
      ))}

      {/* X-axis tick labels (rotated -30deg on mobile so 7 labels don't collide) */}
      {xLabels
        .filter((l) => l.show)
        .map((l, i) => (
          <div
            key={i}
            className="absolute z-[1] origin-top-left -rotate-[30deg] sm:rotate-0 sm:origin-center sm:-translate-x-1/2"
            style={{
              left: xPct(l.vbX),
              top: yPct(VB_H - PAD_B + 6),
            }}
          >
            <span className="block text-[10px] sm:text-[11px] font-mono-acc text-zinc-500 whitespace-nowrap">
              {l.x}
            </span>
          </div>
        ))}

      {/* Y-axis title (vertical) — hidden on mobile to avoid colliding with tick numbers */}
      {yLabel && (
        <div
          className="hidden md:block absolute z-[1] text-[9px] sm:text-[10px] font-mono-acc uppercase tracking-[0.18em] text-zinc-400 whitespace-nowrap origin-top-left"
          style={{
            left: 0,
            top: yPct(PAD_T + INNER_H / 2),
            transform: "rotate(-90deg) translateX(-50%)",
          }}
        >
          {yLabel}
        </div>
      )}

      {/* X-axis title — hidden on mobile (the chart card title above already says this) */}
      {xLabel && (
        <div
          className="hidden md:block absolute z-[1] left-0 right-0 bottom-0 text-center text-[9px] sm:text-[10px] font-mono-acc uppercase tracking-[0.18em] text-zinc-400"
        >
          {xLabel}
        </div>
      )}
    </div>
  );
}

function formatK(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return String(Math.round(n));
}
